/* The frame carries the right-edge shadow, so it stays put while the scroll area inside moves. */
.frame {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.frame[data-more]::after {
  content: "";
  position: absolute;
  z-index: 4;
  top: 0;
  right: 0;
  bottom: 0;
  width: 24px;
  /* A fade into the card, not a shadow: a column that is cut reads as "more" and not as broken. */
  background: linear-gradient(to right, transparent, var(--panel) 90%);
  pointer-events: none;
}

/* `position: relative` makes the scroll area the containing block of the visually hidden captions,
   labels and live regions that sit inside the table. Without it each of them is positioned against the
   frame, outside this scroller, so one far down a long table adds its offset to the frame's own
   scrollable overflow and the card that clips the frame reports content lost beyond its edge. */
.scroll {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: auto;
}

.scroll:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}

/* Every cell is bordered. `border-collapse: collapse` gives one hairline between neighbours. */
.grid {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--t-dense);
  font-variant-numeric: lining-nums tabular-nums;
}

.grid th,
.grid td {
  min-width: 0;
  padding: 8px;
  border: 1px solid var(--line-2);
  text-align: left;
  vertical-align: middle;
}

/* The tinted header row, held at the top of the scroll area. */
.grid thead th {
  position: sticky;
  z-index: 1;
  top: 0;
  background: var(--panel-3);
  color: var(--ink);
  font-size: var(--t-min);
  font-weight: 650;
  white-space: nowrap;
}

.headInner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.grid th[data-align="right"] .headInner {
  flex-direction: row-reverse;
}

.sortButton {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: var(--target-min);
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.sortButton:hover {
  color: var(--accent);
}

.sortButton:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
  border-radius: 3px;
}

.cell[data-align="right"],
.grid th[data-align="right"] {
  text-align: right;
}

.cell[data-align="center"],
.grid th[data-align="center"] {
  text-align: center;
}

.cell {
  color: var(--ink);
}

/* A cell is one line. A long name is cut with an ellipsis (the whole text is its title) rather than
   wrapped into three lines or squeezed until a word breaks in half: a row that grows to the length of
   its longest value is what made a table of two thousand rows ninety pixels a row and unreadable. The
   first column may be longer than the rest; a column that holds a sentence opts back in with `wrap`. */
.grid td.cell,
.grid th.cell {
  max-width: var(--cell-max, 34ch);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.grid td.cell[data-first],
.grid th.cell[data-first] {
  max-width: var(--cell-max-first, 52ch);
}

/* A block inside a cell (a name over a size, a two-line period) is cut the same way. */
.grid .cell > div,
.grid .cell > p {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* A control is never cut: a cell that holds a button or a field keeps its whole width. */
.grid td.cell:has(button, input, select, textarea) {
  max-width: none;
  overflow: visible;
  text-overflow: clip;
}

.grid td.cell[data-wrap],
.grid th.cell[data-wrap] {
  max-width: 56ch;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  overflow-wrap: break-word;
}

/* A bare link in a cell is the product's link colour, not the browser's blue. */
.cell a:not([class]) {
  color: var(--accent);
  text-underline-offset: 3px;
}

/* The tick-box column and the row-number column: narrow, shaded like a spreadsheet's gutter. */
/* The tick-box column is pinned to the left, over a solid fill so the cells that scroll under it do not
   show through. */
.grid .tick {
  position: sticky;
  z-index: 2;
  left: 0;
  width: var(--tick-w, 36px);
  min-width: var(--tick-w, 36px);
  padding-inline: 0;
  background: var(--panel);
  text-align: center;
}

.grid thead .tick {
  z-index: 3;
  background: var(--panel-3);
}

.tick input {
  width: 15px;
  height: 15px;
  margin: 0;
  accent-color: var(--accent);
  cursor: pointer;
}

.grid .number {
  position: sticky;
  z-index: 2;
  left: var(--number-left, 0);
  width: 44px;
  min-width: 44px;
  background: var(--panel-2);
  color: var(--muted);
  font-size: var(--t-min);
  text-align: right;
}

.grid thead .number {
  z-index: 3;
  background: var(--panel-3);
}

/* The first data column keeps its place while the figures scroll past it. */
.grid .pinned {
  position: sticky;
  z-index: 2;
  left: var(--pinned-left, 0);
  background: var(--panel);
}

.grid thead .pinned {
  z-index: 3;
  background: var(--panel-3);
}

.grid tfoot .pinned {
  background: var(--panel-3);
}

.zebra tbody tr:nth-child(even) .pinned,
.zebra tbody tr:nth-child(even) .tick {
  background: var(--panel-2);
}

.grid tbody tr[data-selected] .pinned,
.grid tbody tr[data-selected] .tick {
  background: var(--accent-w);
}

.openable:hover .pinned,
.openable:hover .tick {
  background: var(--accent-w);
}

.zebra tbody tr:nth-child(even) .cell {
  background: var(--panel-2);
}

.grid tbody tr[data-selected] .cell {
  background: var(--accent-w);
}

.openable {
  cursor: pointer;
}

.openable:hover .cell {
  background: var(--accent-w);
}

.openable:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}

.grid tfoot td {
  background: var(--panel-3);
  font-weight: 650;
}

.empty {
  padding: 0;
  background: var(--panel);
}

/* ---- density: the row heights the table research settled on (28, 36, 48) ---------------------- */

.grid[data-density="compact"] th,
.grid[data-density="compact"] td {
  height: 28px;
  padding-block: 3px;
}

.grid[data-density="comfortable"] th,
.grid[data-density="comfortable"] td {
  height: 36px;
  padding-block: 7px;
}

.grid[data-density="spacious"] th,
.grid[data-density="spacious"] td {
  height: 48px;
  padding-block: 12px;
}

/* ---- header: the column menu's chevron shows on hover and focus -------------------------------- */

.grid th:hover [data-col-menu],
.grid th:focus-within [data-col-menu],
.grid th [data-col-menu][aria-expanded="true"] {
  opacity: 1;
}

.sortLevel {
  display: inline-grid;
  place-items: center;
  min-width: 14px;
  height: 14px;
  border-radius: var(--radius-pill);
  background: var(--accent);
  color: var(--on-fill);
  font-size: var(--t-min);
  line-height: 1;
}

/* ---- cells reached by the arrow keys ------------------------------------------------------------ */

.grid td[data-cell]:focus-visible,
.grid th[data-cell]:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}

/* ---- the peek icon: on hover and focus, and gone while a selection shows the bulk bar ---------- */

.tick {
  white-space: nowrap;
}

.peekIcon {
  display: inline-grid;
  place-items: center;
  width: var(--target-min);
  height: var(--target-min);
  margin-left: 2px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-ctl);
  background: none;
  color: var(--muted);
  opacity: 0;
  cursor: pointer;
}

.grid tbody tr:hover .peekIcon,
.grid tbody tr:focus-within .peekIcon,
.grid tbody tr[data-peeked] .peekIcon {
  opacity: 1;
}

.peekIcon:hover:not(:disabled) {
  background: var(--panel-3);
  color: var(--ink);
}

.peekIcon:disabled {
  visibility: hidden;
  cursor: default;
}

.grid tbody tr[data-peeked] .cell,
.grid tbody tr[data-peeked] .pinned,
.grid tbody tr[data-peeked] .tick {
  background: var(--accent-w);
}

/* ---- group rows ------------------------------------------------------------------------------- */

.groupRow th,
.groupRow td {
  background: var(--panel-3);
  font-weight: 650;
}

.grid tbody .groupRow .pinned,
.grid tbody .groupRow .tick {
  background: var(--panel-3);
}

.groupToggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.groupToggle:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
  border-radius: 3px;
}

.groupCount {
  min-width: 20px;
  padding: 0 6px;
  border-radius: var(--radius-pill);
  background: var(--panel);
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 600;
  text-align: center;
}

/* ---- the error row ---------------------------------------------------------------------------- */

.errorRow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px 12px;
  color: var(--crit);
  font-size: var(--t-dense);
  font-weight: 600;
}

/* ---- windowing: the rows above and below the ones drawn stand in as two spacer rows -------------- */

.grid tbody tr[data-spacer],
.grid tbody tr[data-spacer] td {
  height: auto;
  padding: 0;
  border: 0;
  background: transparent;
}

/* ---- fixed tracks (U6, 2026-11-03): a table that budgets its columns fits its card ----------------
   A table whose columns state a `width` is laid out on those tracks (the `<colgroup>` carries them), so
   its width is the card's and not the sum of its longest cells. The one column that states none takes
   what is left. Every body cell is one line, cut with an ellipsis and carrying its whole text as a title
   (`cutTitle`); a column that holds a sentence opts back in with `wrap`. The row is a fixed height: no
   cell may stack two lines or two buttons, which is what made a list of fifty rows eighty pixels each. */
.grid[data-fit] {
  table-layout: fixed;
  width: 100%;
}

.grid[data-fit] td.cell,
.grid[data-fit] th.cell {
  max-width: none;
}

.grid[data-fit] td.cell[data-wrap] {
  overflow: hidden;
  overflow-wrap: anywhere;
}

/* A button that holds a sentence is a link to it: it wraps with the cell and reads from the left, as a
   button's own centred text would not. */
.grid td.cell[data-wrap] > button {
  text-align: left;
  white-space: normal;
}

.grid[data-fit] th,
.grid[data-fit] td {
  padding-inline: 8px;
}

/* A header spends its width on its name. The column menu's chevron floats over the header's right edge on
   hover and focus (it used to hold 30px of every header open for itself), and a column that is not sorted
   shows its sort arrows on hover and focus only. */
.grid[data-fit] th span:has(> [data-col-menu]) {
  position: absolute;
  top: 50%;
  right: 2px;
  transform: translateY(-50%);
}

.grid[data-fit] th [data-col-menu] {
  background: var(--panel-3);
}

/* A header that carries an info control keeps its own room for the floating chevron: without it the
   chevron lies over the info button on hover and focus, and the button that took focus is covered. */
.grid[data-fit] th:has(button[data-info-key]) {
  padding-inline-end: 28px;
}

/* ...and its sort arrows do not appear on hover or focus: they would widen the sort button by sixteen
   pixels at the moment the header takes focus and push the info button back under the chevron. */
.grid[data-fit] th:has(button[data-info-key]) .sortButton [data-sort-neutral] {
  display: none;
}

.grid[data-fit] .sortButton [data-sort-neutral] {
  display: none;
}

.grid[data-fit] th:hover .sortButton [data-sort-neutral],
.grid[data-fit] th:focus-within .sortButton [data-sort-neutral] {
  display: block;
}

/* The default row is 40px whatever a cell holds: a chip, a button and a line of text all sit in it, so a
   list of fifty rows is fifty rows of one height. Compact and spacious (the density control) still win. */
.grid[data-fit]:not([data-density="compact"], [data-density="spacious"]) td,
.grid[data-fit]:not([data-density="compact"], [data-density="spacious"]) th {
  height: 40px;
  padding-block: 6px;
}

.grid[data-fit] th.tick,
.grid[data-fit] td.tick {
  padding-inline: 0;
}
